import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { urls } from './config.dev';
import './viewfile.css';
import TreatmentChatModal from './TreatmentChatModal';
import Topbar from './Topbar';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ViewFiles() {
    const [fileData, setFileData] = useState(null);
    const [editableFields, setEditableFields] = useState({});
    const [showTreatmentChatModal, setShowTreatmentChatModal] = useState(false);
    const [urlToken, setUrlToken] = useState('');
    const [employeeName, setEmployeeName] = useState('');
    const [loading, setLoading] = useState(false);
    const [updatingField, setUpdatingField] = useState(null);
    
    const treatmentChatModalRef = useRef(null);
    const signsAndSymptomsRef = useRef(null);
    const clinicalNotesRef = useRef(null);
    const diagnosisRef = useRef(null);
    const treatmentNotesRef = useRef(null);
    const treatmentPlanRef = useRef(null);
    
    const { token, fileId } = useParams();
    const navigate = useNavigate();

    // Extract token from URL
    useEffect(() => {
        const extractToken = () => {
            try {
                const url = window.location.href;
                const extractedToken = url.split('/').slice(-2, -1)[0];
                setUrlToken(extractedToken);
            } catch (error) {
                console.error("Error extracting token from URL:", error);
            }
        };
        extractToken();
    }, []);

    // Fetch Employee Name and Security Check
    useEffect(() => {
        const fetchEmployeeName = async () => {
            try {
                const response = await fetch(urls.security, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token }),
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.message === 'Session valid') {
                        setEmployeeName(data.employee_name);
                        localStorage.setItem('token', data.clinic_session_token);
                    } else if (data.error === 'Session expired') {
                        navigate(`/dashboard?token=${data.clinic_session_token}`);
                    } else {
                        navigate('/login');
                    }
                } else {
                    throw new Error('Failed to perform security check');
                }
            } catch (error) {
                console.error('Error performing security check:', error);
                navigate('/login');
            }
        };

        fetchEmployeeName();
    }, [navigate, token]);

    // Fetch File Data at intervals
    useEffect(() => {
        const fetchFileData = () => {
            fetch(`${urls.viewfile}?fileId=${fileId}`)
                .then(response => response.json())
                .then(data => {
                    setFileData(data);
                    setEditableFields({
                        blood_pressure: data.fileData?.blood_pressure,
                        temperature: data.fileData?.temperature,
                        spo2: data.fileData?.spo2,
                        body_weight: data.fileData?.body_weight,
                        signs_and_symptoms: data.fileData?.signs_and_symptoms,
                        clinical_notes: data.fileData?.clinical_notes,
                        diagnosis: data.fileData?.diagnosis,
                        treatment_plan: data.fileData?.treatment_plan,
                        treatment_notes: data.fileData?.treatment_notes || '',
                    });
                })
                .catch(error => {
                    console.error('Error fetching file data:', error);
                });
        };

        fetchFileData();
        const interval = setInterval(fetchFileData, 5000);

        return () => clearInterval(interval);
    }, [fileId]);

    // Auto-adjust textarea heights
    useEffect(() => {
        const adjustHeight = (ref) => {
            if (ref.current) {
                ref.current.style.height = 'auto';
                ref.current.style.height = `${ref.current.scrollHeight}px`;
            }
        };
    
        adjustHeight(signsAndSymptomsRef);
        adjustHeight(clinicalNotesRef);
        adjustHeight(diagnosisRef);
        adjustHeight(treatmentNotesRef);
        adjustHeight(treatmentPlanRef);
    }, [fileData]);

    // Handle Treatment Chat Modal
    const handleOpenTreatmentChatModal = () => {
        setShowTreatmentChatModal(true);
    };

    const handleCloseTreatmentChatModal = () => {
        setShowTreatmentChatModal(false);
    };

    // Navigation functions
    const navigateToDischargeForm = () => {
        navigate(`/discharge-form/${employeeName}/${fileId}`);
    };

    const navigateToReferralForm = () => {
        navigate(`/referral-form/${employeeName}/${fileId}`);
    };

    // Print function
    const handlePrint = async () => {
        try {
            const response = await axios.post(urls.printfile, {
                fileId,
                token,
            }, {
                responseType: 'blob',
            });
    
            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const pdfUrl = URL.createObjectURL(pdfBlob);
    
            const newWindow = window.open(pdfUrl);
            if (newWindow) {
                newWindow.focus();
            } else {
                alert('Please allow popups for this website to view the PDF.');
            }
        } catch (error) {
            console.error('Error fetching PDF:', error);
        }
    };

    // Helper components
    const InfoItem = ({ label, value, span = 1 }) => (
        <div style={{ gridColumn: `span ${span}` }}>
            <p style={{ 
                margin: 0,
                fontSize: '14px',
                color: '#7f8c8d'
            }}>{label}</p>
            <p style={{ 
                margin: '4px 0 0 0',
                fontSize: '15px',
                fontWeight: '500',
                color: '#2c3e50'
            }}>{value || '-'}</p>
        </div>
    );

    const ClinicalSection = ({ title, value, textAreaRef, placeholder }) => (
        <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            padding: '20px'
        }}>
            <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2c3e50',
                marginTop: 0,
                marginBottom: '15px'
            }}>{title}</h2>
            <textarea
                ref={textAreaRef}
                value={value || ''}
                readOnly
                placeholder={placeholder}
                style={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '16px',
                    resize: 'vertical',
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'default'
                }}
            />
        </div>
    );

    // Style objects
    const printButtonStyle = {
        backgroundColor: '#fff',
        color: '#3498db',
        border: '1px solid #3498db',
        borderRadius: '4px',
        padding: '8px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '5px'
    };

    const actionButtonStyle = {
        backgroundColor: '#3498db',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '5px'
    };

    const treatmentNotesTextAreaStyle = {
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        resize: 'vertical',
        width: '100%',
        minHeight: '120px',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        backgroundColor: '#fff',
        cursor: 'default'
    };

    const buttonStyle = {
        backgroundColor: '#3498db',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        marginTop: '10px'
    };

    if (!fileData) {
        return <div>Loading...</div>;
    }

    return (
        <div className="view-file-container" style={{
            margin: '25px auto',
            marginTop: '25px',
            fontFamily: '"Segoe UI", Roboto, sans-serif',
            color: '#333',
            lineHeight: '1.6'
        }}>
            <Topbar token={urlToken} />
            <ToastContainer />
            
            <div className="file-details" style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                padding: '30px'
            }}>
                {/* Header Section */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: '15px',
                    marginBottom: '25px'
                }}>
                    <h1 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        margin: 0
                    }}>Patient Record - {fileData.fileData?.date_time}</h1>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={handlePrint} style={printButtonStyle}>Print File</button>
                        <button onClick={navigateToDischargeForm} style={actionButtonStyle}>Discharge Form</button>
                        <button onClick={navigateToReferralForm} style={actionButtonStyle}>Referral Form</button>
                    </div>
                </div>

                {/* Patient Information Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '30px',
                    marginBottom: '30px'
                }}>
                    {/* Patient Demographics */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        padding: '20px',
                    }}>
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#2c3e50',
                            marginTop: 0,
                            marginBottom: '15px'
                        }}>Patient Information</h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px'
                        }}>
                            <InfoItem label="Name" value={`${fileData.fileData?.first_name} ${fileData.fileData?.last_name}`} />
                            <InfoItem label="OPD No" value={fileData.fileData?.opd_no} />
                            <InfoItem label="Phone" value={fileData.fileData?.phone_number} />
                            <InfoItem label="Age" value={`${fileData.fileData?.age} years`} />
                            <InfoItem label="Sex" value={fileData.fileData?.sex} />
                            <InfoItem label="Address" value={fileData.fileData?.address} span={2} />
                        </div>
                    </div>

                    {/* Vitals */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        padding: '20px',
                    }}>
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#2c3e50',
                            marginTop: 0,
                            marginBottom: '15px'
                        }}>Vital Signs</h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px'
                        }}>
                            <InfoItem label="BP" value={fileData.fileData?.blood_pressure} />
                            <InfoItem label="Temp" value={fileData.fileData?.temperature} />
                            <InfoItem label="SPO2" value={fileData.fileData?.spo2} />
                            <InfoItem label="Pulse" value={fileData.fileData?.pulse_rate} />
                            <InfoItem label="Resp. Rate" value={fileData.fileData?.respiratory_rate} />
                            <InfoItem label="Weight" value={fileData.fileData?.body_weight} />
                        </div>
                    </div>
                </div>

                {/* Clinical Sections */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: '25px'
                }}>
                    {/* Presenting Complaints */}
                    <ClinicalSection 
                        title="Presenting Complaints"
                        value={editableFields.signs_and_symptoms}
                        textAreaRef={signsAndSymptomsRef}
                        placeholder="No presenting complaints recorded"
                    />

                    {/* Clinical Notes */}
                    <ClinicalSection 
                        title="Clinical Notes"
                        value={editableFields.clinical_notes}
                        textAreaRef={clinicalNotesRef}
                        placeholder="No clinical notes recorded"
                    />

                    {/* Diagnosis */}
                    <ClinicalSection 
                        title="Diagnosis"
                        value={editableFields.diagnosis}
                        textAreaRef={diagnosisRef}
                        placeholder="No diagnosis inserted by the doctor yet"
                    />
                </div>

                {/* Treatment Plan Section */}
                <div style={{
                    marginTop: '30px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    padding: '20px',
                }}>
                    <h2 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginTop: 0,
                        marginBottom: '15px'
                    }}>Treatment Plan</h2>
                    
                    <textarea
                        ref={treatmentPlanRef}
                        value={editableFields.treatment_plan || ''}
                        readOnly
                        placeholder="No treatment prescribed for this patient yet"
                        style={{
                            fontFamily: 'Arial, sans-serif',
                            fontSize: '16px',
                            resize: 'vertical',
                            width: '100%',
                            minHeight: '100px',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: '#fff',
                            cursor: 'default'
                        }}
                    />
                </div>

                {/* Investigations Section */}
                <div style={{
                    marginTop: '30px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '25px'
                }}>
                    {/* Lab Tests */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        padding: '20px',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '15px'
                        }}>
                            <h2 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#2c3e50',
                                margin: 0
                            }}>Laboratory Tests Done</h2>
                        </div>
                        
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '15px',
                            borderRadius: '4px',
                            border: '1px solid #eee',
                            minHeight: '60px'
                        }}>
                            {fileData.fileData?.lab_tests?.length > 0 ? (
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {fileData.fileData.lab_tests.map((test, index) => (
                                        <li key={index}>{test}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p style={{ color: '#7f8c8d', margin: 0 }}>No lab tests ordered</p>
                            )}
                        </div>

                        {/* Lab Status */}
                        <div style={{
                            marginTop: '15px',
                            padding: '12px',
                            backgroundColor: fileData.labStatusMessage ? '#e8f4fd' : '#f5f5f5',
                            borderRadius: '4px',
                        }}>
                            <p style={{ 
                                margin: 0,
                                fontWeight: fileData.labStatusMessage ? '500' : '400',
                                color: fileData.labStatusMessage ? '#2c3e50' : '#7f8c8d'
                            }}>
                                <strong>Lab Status:</strong> {fileData.labStatusMessage || 'No current status'}
                            </p>
                        </div>
                    </div>

                    {/* Radiology Tests */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        padding: '20px',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '15px'
                        }}>
                            <h2 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#2c3e50',
                                margin: 0
                            }}>Radiology Exams Done</h2>
                        </div>
                        
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '15px',
                            borderRadius: '4px',
                            border: '1px solid #eee',
                            minHeight: '60px'
                        }}>
                            {fileData.fileData?.radiology_exams?.length > 0 ? (
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {fileData.fileData.radiology_exams.map((test, index) => (
                                        <li key={index}>{test}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p style={{ color: '#7f8c8d', margin: 0 }}>No radiology exams ordered</p>
                            )}
                        </div>

                        {/* Radiology Status */}
                        <div style={{
                            marginTop: '15px',
                            padding: '12px',
                            backgroundColor: fileData.radiologyStatusMessage ? '#e8f4fd' : '#f5f5f5',
                            borderRadius: '4px'
                        }}>
                            <p style={{ 
                                margin: 0,
                                fontWeight: fileData.radiologyStatusMessage ? '500' : '400',
                                color: fileData.radiologyStatusMessage ? '#2c3e50' : '#7f8c8d'
                            }}>
                                <strong>Radiology Status:</strong> {fileData.radiologyStatusMessage || 'No current status'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Treatment Notes Section */}
                <div style={{
                    marginTop: '30px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    padding: '20px',
                }}>
                    <h2 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginTop: 0,
                        marginBottom: '15px'
                    }}>Drugs Given / Treatment Chat</h2>
                    
                    <textarea
                        ref={treatmentNotesRef}
                        value={editableFields.treatment_notes || ''}
                        readOnly
                        style={treatmentNotesTextAreaStyle}
                        placeholder="No treatment chat recorded yet"
                    />
                    
                    <button
                        onClick={handleOpenTreatmentChatModal}
                        style={buttonStyle}
                    >
                        Open Treatment Chat
                    </button>
                </div>
            </div>

            {/* Treatment Chat Modal */}
            {showTreatmentChatModal && (
                <div ref={treatmentChatModalRef} className="treatment-chat-modal open">
                    <div className="treatment-chat-modal-content">
                        <TreatmentChatModal
                            fileId={fileId}
                            employeeName={employeeName}
                            onClose={handleCloseTreatmentChatModal}
                            token={token}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default ViewFiles;